import { useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import toast from 'react-hot-toast';
import {
  ButtonGroup,
  Container,
  Dropdown,
  Grid,
  Header,
  Loader,
  Message,
  Pagination,
} from 'semantic-ui-react';
import ReactGA from 'react-ga4';
import { format, parseISO } from 'date-fns';

import { getAdminUsersPaginated, patchUserActive } from '~/api/admin';
import { Button, Table } from '~/components';

import UsersFilters, { type AccountStatusFilter } from './UsersFilters';
import EditAdminUserModal from './EditAdminUserModal';
import { buildSpoofHref, formatUserListLastAccess, stopRowClick } from '~/features/admin/adminUserListShared';

import '~/features/projectSearch/styles.scss';
import '~/features/bidSearch/styles.scss';
import '../companies/styles.scss';

const PAGE_SIZE = 50;
const USER_FILTERS_DEBOUNCE_MS = 400;

function truncateForMeta(s: string, max = 56): string {
  if (s.length <= max) {
    return s;
  }
  return `${s.slice(0, max - 1)}…`;
}

function formatLastAccessAt(iso: string): string {
  return formatUserListLastAccess(iso);
}

function formatCreatedDate(iso: string): string {
  return format(parseISO(iso), 'MM/dd/yy');
}

function UserActionsCell({
  email,
  locationSearch,
  selected,
  userId,
  isActive,
  togglePending,
  onToggleActive,
  onOpenEdit,
}: {
  email: string;
  locationSearch: string;
  selected: boolean;
  userId: string;
  isActive: boolean;
  togglePending: boolean;
  onToggleActive: () => void;
  onOpenEdit: () => void;
}) {
  const spoofHref = buildSpoofHref(email, locationSearch);

  return (
    <Table.Cell
      textAlign='center'
      onClick={stopRowClick}
    >
      <ButtonGroup secondary size='tiny'>
        <Button
          as='a'
          secondary
          href={spoofHref}
          target='_blank'
          onClick={stopRowClick}
          style={{
            padding: '8px 12px'
          }}
        >
          Spoof
        </Button>
        <Dropdown
          className='button icon'
          icon='dropdown'
          trigger={<></>}
          onClick={stopRowClick}
          style={{
            padding: '8px 12px',
            verticalAlign: 'middle',
            alignItems: 'center',
            display: 'flex',
            borderLeft: '1px solid #00446c',
          }}
        >
          <Dropdown.Menu>
            <Dropdown.Item
              text='Update'
              onClick={() => {
                onOpenEdit();
              }}
            />
            <Dropdown.Item
              text={isActive ? 'Deactivate' : 'Activate'}
              disabled={togglePending}
              onClick={e => {
                onToggleActive();
              }}
            />
          </Dropdown.Menu>
        </Dropdown>
      </ButtonGroup>
    </Table.Cell>
  );
}

type SortColumn =
  | 'name'
  | 'email'
  | 'companyName'
  | 'role'
  | 'createdAt'
  | 'lastAccess';

export default function AdminUsers() {
  const location = useLocation();
  const queryClient = useQueryClient();
  const [selectedRow, setSelectedRow] = useState<string | number | null>(null);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortColumn, setSortColumn] = useState<SortColumn>('lastAccess');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [searchFilter, setSearchFilter] = useState('');
  const [companyNameFilter, setCompanyNameFilter] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [debouncedCompanyName, setDebouncedCompanyName] = useState('');
  const [rolesFilter, setRolesFilter] = useState<string[]>([]);
  const [accountStatusFilter, setAccountStatusFilter] = useState<AccountStatusFilter>(null);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setDebouncedSearch(searchFilter);
      setDebouncedCompanyName(companyNameFilter);
    }, USER_FILTERS_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
  }, [searchFilter, companyNameFilter]);

  const searchParam = debouncedSearch.trim();
  const companyNameParam = debouncedCompanyName.trim();

  const filterSignature = useMemo(
    () =>
      [searchParam, companyNameParam, [...rolesFilter].sort().join(','), accountStatusFilter].join('|'),
    [searchParam, companyNameParam, rolesFilter, accountStatusFilter]
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [filterSignature]);

  const rolesParam =
    rolesFilter.length > 0 ? [...rolesFilter].sort().join(',') : undefined;
  const activeParam =
    accountStatusFilter === 'active' ? 'true' : accountStatusFilter === 'inactive' ? 'false' : undefined;

  const hasFilters =
    searchFilter.length > 0 ||
    companyNameFilter.length > 0 ||
    rolesFilter.length > 0 ||
    accountStatusFilter != null;

  const clearAllFilters = () => {
    setSearchFilter('');
    setCompanyNameFilter('');
    setRolesFilter([]);
    setAccountStatusFilter(null);
  };

  const queryKey = [
    'adminUsersPage',
    currentPage,
    sortColumn,
    sortDirection,
    searchParam,
    companyNameParam,
    rolesParam ?? '',
    activeParam ?? '',
  ] as const;

  const { data, status: fetchStatus } = useQuery(
    queryKey,
    () =>
      getAdminUsersPaginated({
        page: currentPage,
        limit: PAGE_SIZE,
        sortProperty: sortColumn,
        sortOrder: sortDirection,
        search: searchParam || undefined,
        companyName: companyNameParam || undefined,
        roles: rolesParam,
        ...(activeParam != null ? { active: activeParam } : {}),
      }),
    { refetchOnWindowFocus: false, keepPreviousData: true }
  );

  const [togglingUserId, setTogglingUserId] = useState<string | null>(null);

  const { mutate: toggleUserActive } = useMutation(
    ({ userId, nextActive }: { userId: string; nextActive: boolean }) => patchUserActive(userId, nextActive),
    {
      onMutate: ({ userId }) => setTogglingUserId(userId),
      onSuccess: (_, { nextActive }) => {
        queryClient.invalidateQueries(['adminUsersPage']);
        toast.success(nextActive ? 'User activated.' : 'User deactivated.');
      },
      onError: (err: any) => {
        const msg =
          err?.response?.data?.message ?? err?.response?.data?.errors?.[0]?.message ?? err?.message;
        toast.error(typeof msg === 'string' ? msg : 'Failed to update user.');
      },
      onSettled: () => setTogglingUserId(null),
    }
  );

  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(totalPages);
    }
  }, [currentPage, totalPages]);

  useEffect(() => {
    ReactGA.send({
      hitType: 'pageview',
      page: '/users',
      title: 'Users | Conductor Solar',
    });
    document.title = 'Users | Conductor Solar';
  }, []);

  const getSort = (column: SortColumn) => {
    const sort = sortColumn === column ? sortDirection : undefined;
    if (sort === 'asc') return 'ascending';
    if (sort === 'desc') return 'descending';
    return sort;
  };

  const updateSort = (column: SortColumn) => {
    setCurrentPage(1);
    if (sortColumn === column) {
      setSortDirection(d => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const users = data?.users ?? [];
  const isLoading = fetchStatus === 'loading';
  const showInitialLoader = fetchStatus === 'loading' && !data;
  const showEmpty = fetchStatus === 'success' && total === 0;
  const showTable = data != null && total > 0;

  const usersOnPage = users.length;
  const rangeStart = total === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const rangeEnd = total === 0 ? 0 : Math.min((currentPage - 1) * PAGE_SIZE + usersOnPage, total);

  const activeFilterDescription = useMemo(() => {
    const parts: string[] = [];
    if (searchParam) {
      parts.push(`Name or email contains “${truncateForMeta(searchParam)}”`);
    }
    if (companyNameParam) {
      parts.push(`Company contains “${truncateForMeta(companyNameParam)}”`);
    }
    if (rolesFilter.length > 0) {
      parts.push(`Role: ${[...rolesFilter].sort().join(', ')}`);
    }
    if (accountStatusFilter === 'active') {
      parts.push('Account: Active only');
    } else if (accountStatusFilter === 'inactive') {
      parts.push('Account: Deactivated only');
    }
    return parts.length > 0 ? parts.join(' · ') : null;
  }, [searchParam, companyNameParam, rolesFilter, accountStatusFilter]);

  return (
    <div className='project-search-container bid-search-container'>
      <Container fluid>
        <Grid centered>
          <Grid.Column mobile={12} tablet={12} computer={12} largeScreen={12}>
            <Header as='h1' textAlign='left' style={{ marginBottom: '0.75rem' }}>
              Users
            </Header>

            <div className='card'>
              <UsersFilters
                search={searchFilter}
                onSearchChange={setSearchFilter}
                companyName={companyNameFilter}
                onCompanyNameChange={setCompanyNameFilter}
                roles={rolesFilter}
                onRolesChange={setRolesFilter}
                accountStatus={accountStatusFilter}
                onAccountStatusChange={setAccountStatusFilter}
              />
            </div>

            <div className='card'>
              {showInitialLoader && <Loader active inline='centered' />}
              {showEmpty && <Message info>No users found matching your filters.</Message>}
              {showTable && (
                <>
                  {isLoading && <Table.Loader />}
                  <div
                    style={
                      isLoading
                        ? {
                          marginTop: 0,
                          minHeight: 'calc(100vh - 16rem)',
                          background: 'var(--color-pure-white)',
                        }
                        : {}
                    }
                  >
                    <div className='admin-companies-search-meta'>
                      Showing {rangeStart} - {rangeEnd} of {total}{' '}
                      {total === 1 ? 'user' : 'users'}
                      {hasFilters && (
                        <>
                          {' - '}
                          <button
                            type='button'
                            className='admin-companies-clear-link'
                            onClick={clearAllFilters}
                          >
                            Clear filters
                          </button>
                        </>
                      )}
                      {activeFilterDescription && (
                        <>
                          <br />
                          {activeFilterDescription}
                        </>
                      )}
                    </div>
                    <Table className='admin-companies-table' striped sortable textAlign='left'>
                      <Table.Header>
                        <Table.Row>
                          <Table.HeaderCell
                            sorted={getSort('name')}
                            onClick={() => updateSort('name')}
                          >
                            Name
                          </Table.HeaderCell>
                          <Table.HeaderCell
                            sorted={getSort('email')}
                            onClick={() => updateSort('email')}
                          >
                            Email
                          </Table.HeaderCell>
                          <Table.HeaderCell
                            sorted={getSort('companyName')}
                            onClick={() => updateSort('companyName')}
                          >
                            Company
                          </Table.HeaderCell>
                          <Table.HeaderCell sorted={getSort('role')} onClick={() => updateSort('role')}>
                            Role
                          </Table.HeaderCell>
                          <Table.HeaderCell
                            sorted={getSort('createdAt')}
                            onClick={() => updateSort('createdAt')}
                          >
                            Created
                          </Table.HeaderCell>
                          <Table.HeaderCell
                            sorted={getSort('lastAccess')}
                            onClick={() => updateSort('lastAccess')}
                          >
                            Last Access
                          </Table.HeaderCell>
                          <Table.HeaderCell style={{ textAlign: 'center' }}>Actions</Table.HeaderCell>
                        </Table.Row>
                      </Table.Header>

                      <Table.Body>
                        {users.map((d: any) => (
                          <Table.Row
                            key={d.id}
                            onClick={() => setSelectedRow(d.id)}
                          >
                            <Table.Cell>
                              <span className='admin-companies-name-cell'>
                                <span>{d.name}</span>
                                {d.active === false && (
                                  <span className='admin-companies-deactivated-badge'>Deactivated</span>
                                )}
                              </span>
                            </Table.Cell>
                            <Table.Cell>
                              {d.email}
                            </Table.Cell>
                            <Table.Cell onClick={stopRowClick}>
                              {d.companyId ? (
                                <a
                                  href={`/company-profile/${d.companyId}`}
                                  onClick={stopRowClick}
                                >
                                  {d.companyName ?? '—'}
                                </a>
                              ) : (
                                (d.companyName ?? '—')
                              )}
                            </Table.Cell>
                            <Table.Cell>
                              {d.role === 'INSTALLER' ? 'DEVELOPER' : d.role}
                            </Table.Cell>
                            <Table.Cell>
                              {d.createdAt ? formatCreatedDate(d.createdAt) : null}
                            </Table.Cell>
                            <Table.Cell>
                              {d.lastAccess ? formatLastAccessAt(d.lastAccess) : null}
                            </Table.Cell>
                            <UserActionsCell
                              email={d.email}
                              locationSearch={location.search}
                              selected={selectedRow === d.id}
                              userId={d.id}
                              isActive={d.active !== false}
                              togglePending={togglingUserId === d.id}
                              onToggleActive={() =>
                                toggleUserActive({
                                  userId: d.id,
                                  nextActive: d.active === false,
                                })
                              }
                              onOpenEdit={() => setEditingUserId(d.id)}
                            />
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table>
                    {totalPages > 1 && (
                      <div
                        style={{
                          marginTop: '2rem',
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <Pagination
                          activePage={currentPage}
                          onPageChange={(_, { activePage }) => setCurrentPage(activePage as number)}
                          totalPages={totalPages}
                          siblingRange={1}
                          boundaryRange={1}
                          ellipsisItem={null}
                          firstItem={null}
                          lastItem={null}
                          prevItem={{ content: '←', icon: false }}
                          nextItem={{ content: '→', icon: false }}
                        />
                      </div>
                    )}
                  </div>
                </>
              )}
            </div>
          </Grid.Column>
        </Grid>
      </Container>
      {editingUserId != null && (
        <EditAdminUserModal userId={editingUserId} closeModal={() => setEditingUserId(null)} />
      )}
    </div>
  );
}
